import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Pencil,
  RefreshCw,
  Users,
  QrCode,
  Globe,
  Hash,
  DollarSign,
  Palette,
  FileText,
  Printer,
  Map,
} from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './MailerBuilderPage.module.css';

type StepKey = 'Creative' | 'Mailing list' | 'Tracking' | 'Budget';

const STEPS: StepKey[] = ['Creative', 'Mailing list', 'Tracking', 'Budget'];

const MAILER_NAMES: Record<string, string> = {
  rec: 'New Movers 94521',
  imp: 'Implant Awareness — 55+ affluent',
  b2s: 'Back-to-School Family Checkups',
  new: 'New Mailer',
};

export function MailerBuilderPage() {
  const navigate = useNavigate();
  const { mailerId } = useParams<{ mailerId: string }>();
  const name = MAILER_NAMES[mailerId ?? ''] ?? 'Mailer';

  const [approved, setApproved] = useState<Record<StepKey, boolean>>({
    Creative: false,
    'Mailing list': false,
    Tracking: true,
    Budget: false,
  });

  const toggle = (step: StepKey) =>
    setApproved((prev) => ({ ...prev, [step]: !prev[step] }));

  const allDone = approved.Creative && approved['Mailing list'] && approved.Budget;

  return (
    <div className={patterns.page}>
      <button className={styles.backLink} onClick={() => navigate('/print-mail/mailers')}>
        <ArrowLeft size={14} aria-hidden />
        Back to Mailers
      </button>

      <PageHeader
        title={name}
        subtitle="Set up the postcard, confirm the mailing list, configure tracking, and approve the budget — then it goes to print."
      />

      {/* Step progress */}
      <div className={styles.progressBar}>
        {STEPS.map((step) => (
          <div
            key={step}
            className={`${styles.progressStep} ${approved[step] ? styles.progressStepDone : ''}`}
          >
            {approved[step] ? (
              <Check size={13} aria-hidden />
            ) : (
              <div className={styles.stepDot} />
            )}
            {step}
          </div>
        ))}
      </div>

      <div className={styles.builderGrid}>
        {/* Left: creative preview */}
        <div className={styles.creativeStack}>
          <div className={patterns.panel}>
            <div className={patterns.kicker}>
              <Palette size={12} aria-hidden />
              Postcard — front
            </div>
            <div className={styles.postcardFront}>
              <div className={styles.postcardBrand}>
                <div className={styles.postcardMark}>◎</div>
                MT. DIABLO FAMILY DENTISTS
              </div>
              <div className={styles.postcardHeadline}>
                New to Concord?
                <br />
                Meet your family dentist.
              </div>
              <div className={styles.postcardBody}>
                Welcoming, unrushed care for the whole family — now accepting new patients.
              </div>
              <div className={styles.postcardCta}>Free new-patient exam</div>
            </div>
          </div>

          <div className={patterns.panel}>
            <div className={patterns.kicker}>
              <FileText size={12} aria-hidden />
              Postcard — back
            </div>
            <div className={styles.postcardBack}>
              <div>
                Dr. Naina Jain & team · 2 cleanings, exam & X-rays for new patients. Walk every
                option, no pressure. Call or scan to book.
                <div className={styles.postcardBackPhone}>(925) 555-0190</div>
                <div className={styles.postcardBackLink}>…practicenova.ai/welcome-94521</div>
              </div>
              <div className={styles.qrPlaceholder}>
                <QrCode size={44} aria-hidden />
                Scan to book
              </div>
            </div>
          </div>

          <Button
            variant={approved.Creative ? 'secondary' : 'primary'}
            leftIcon={<Check size={14} aria-hidden />}
            className={styles.creativeApprove}
            onClick={() => toggle('Creative')}
          >
            {approved.Creative ? 'Creative approved' : 'Approve creative'}
          </Button>

          <div className={styles.creativeActions}>
            <Button variant="secondary" size="sm" leftIcon={<Pencil size={13} aria-hidden />}>
              Edit copy
            </Button>
            <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={13} aria-hidden />}>
              Regenerate
            </Button>
          </div>

          <p className={styles.creativeFn}>
            Copy &amp; styling generated from approved Brand Kit (Warm &amp; Familiar).
          </p>
        </div>

        {/* Right: list, tracking, budget */}
        <div className={styles.rightStack}>
          {/* Mailing list */}
          <div className={patterns.panel}>
            <div className={styles.sectionHeaderRow}>
              <div className={patterns.kicker}>
                <Users size={12} aria-hidden />
                Mailing list &amp; targeting
              </div>
              {approved['Mailing list'] && (
                <span className={patterns.pillSuccess}>
                  <Check size={10} aria-hidden />
                  Confirmed
                </span>
              )}
            </div>
            <div className={styles.audienceGrid}>
              <div>
                <div className={styles.audienceRows}>
                  {[
                    ['Segment', 'New Movers (from Strategy)'],
                    ['Geography', '94521 — Concord'],
                    ['Filter', 'Moved in last 90 days · single-family + multi'],
                    ['Source', 'Census ACS + USPS new-mover (via partner)'],
                  ].map(([label, value]) => (
                    <div key={label} className={styles.audienceRow}>
                      <span className={styles.audienceRowLabel}>{label}</span>
                      <span className={styles.audienceRowValue}>{value}</span>
                    </div>
                  ))}
                </div>
                <div className={styles.householdCount}>
                  <span className={styles.householdLabel}>Matched households</span>
                  <span className={styles.householdValue}>1,200</span>
                </div>
              </div>
              <div className={styles.mapPlaceholder}>
                <Map size={26} aria-hidden />
                <span>Coverage map</span>
              </div>
            </div>
            <div className={styles.audienceActions}>
              <Button variant="secondary" size="sm" leftIcon={<Pencil size={13} aria-hidden />}>
                Adjust audience
              </Button>
              <Button
                variant={approved['Mailing list'] ? 'secondary' : 'primary'}
                size="sm"
                leftIcon={<Check size={13} aria-hidden />}
                onClick={() => toggle('Mailing list')}
              >
                {approved['Mailing list'] ? 'Confirmed' : 'Confirm audience'}
              </Button>
            </div>
          </div>

          {/* Tracking */}
          <div className={patterns.panel}>
            <div className={styles.sectionHeaderRow}>
              <div className={patterns.kicker}>
                <QrCode size={12} aria-hidden />
                Tracking — auto-configured
              </div>
              <span className={patterns.pillSuccess}>
                <Check size={10} aria-hidden />
                Ready
              </span>
            </div>
            <div className={styles.trackingGrid}>
              <div className={`${styles.trackingItem} ${styles.trackingItemCentered}`}>
                <span className={styles.trackingIconSuccess}><QrCode size={40} aria-hidden /></span>
                <div className={styles.trackingItemLabel}>Unique QR</div>
                <div className={styles.trackingItemSub}>scans tracked</div>
              </div>
              <div className={styles.trackingItem}>
                <span className={styles.trackingIconSuccess}><Globe size={18} aria-hidden /></span>
                <div className={styles.trackingItemLabel}>Dedicated landing page</div>
                <div className={styles.trackingItemLink}>…/welcome-94521</div>
                <div className={styles.trackingItemSub}>form fills tracked</div>
              </div>
              <div className={styles.trackingItem}>
                <span className={styles.trackingIconMuted}><Hash size={18} aria-hidden /></span>
                <div className={styles.trackingItemLabel}>Tracked number</div>
                <div className={styles.trackingItemSub}>(925) 555-0190</div>
                <div className={styles.trackingItemSub}>calls → Call Log</div>
              </div>
            </div>
            <p className={styles.trackingNote}>
              Response = QR scans + landing-page form fills + tracked calls. Same number pool as
              ads. Lead-level only.
            </p>
          </div>

          {/* Budget */}
          <div className={patterns.panel}>
            <div className={styles.sectionHeaderRow}>
              <div className={patterns.kicker}>
                <DollarSign size={12} aria-hidden />
                Budget &amp; approval
              </div>
              {approved.Budget && (
                <span className={patterns.pillSuccess}>
                  <Check size={10} aria-hidden />
                  Approved
                </span>
              )}
            </div>
            {[
              ['Quantity', '1,200 pieces'],
              ['Print (at cost)', '$0.41 / pc · $492'],
              ['Postage (EDDM, at cost)', '$0.16 / pc · $192'],
            ].map(([label, value]) => (
              <div key={label} className={styles.budgetRow}>
                <span className={styles.budgetLabel}>{label}</span>
                <span className={styles.budgetValue}>{value}</span>
              </div>
            ))}
            <div className={styles.budgetTotal}>
              <span>Total</span>
              <span>$684</span>
            </div>
            <div className={styles.budgetDisclaimer}>
              Billed at cost through our print/mail partner. No spend is committed until you approve.
            </div>
            <div className={styles.budgetAction}>
              <Button
                variant={approved.Budget ? 'secondary' : 'primary'}
                leftIcon={<Check size={14} aria-hidden />}
                onClick={() => toggle('Budget')}
              >
                {approved.Budget ? 'Budget approved' : 'Approve budget'}
              </Button>
            </div>
          </div>

          {/* Send action */}
          <div className={patterns.panel}>
            <div className={styles.sendRow}>
              <p className={styles.sendRowText}>
                {allDone
                  ? 'All steps complete — ready to send to the print partner.'
                  : 'Approve creative, audience, and budget to send to print.'}
              </p>
              <Button
                variant={allDone ? 'primary' : 'secondary'}
                leftIcon={<Printer size={14} aria-hidden />}
                disabled={!allDone}
              >
                Send to print partner
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
